
import React, { useState } from 'react';
import { SettingsLayout } from '../../components/SettingsLayout';
import { NotificationsOff } from '@mui/icons-material';
import { Loader2, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const NotificationsScreen: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
    }, 1500);
  };

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="p-10 border-b border-slate-100">
                <h2 className="text-2xl font-bold text-slate-900">{t('notificationsSettings.title')}</h2>
                <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">{t('notificationsSettings.subtitle')}</p>
            </div>
            <div className="p-0">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-10 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-[0.1em]">{t('notificationsSettings.table.type')}</th>
                            <th className="px-6 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-[0.1em] text-center w-28">{t('notificationsSettings.table.email')}</th>
                            <th className="px-10 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-[0.1em] text-center w-28">{t('notificationsSettings.table.push')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        <tr className="hover:bg-slate-50/30 transition-colors">
                            <td className="px-10 py-7">
                                <p className="text-[15px] font-semibold text-slate-900">{t('notificationsSettings.listing.title')}</p>
                                <p className="text-sm text-slate-500 mt-1">{t('notificationsSettings.listing.desc')}</p>
                            </td>
                            <td className="px-6 py-7 text-center">
                                <input defaultChecked className="w-5 h-5 cursor-pointer rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/>
                            </td>
                            <td className="px-10 py-7 text-center">
                                <input defaultChecked className="w-5 h-5 cursor-pointer rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/>
                            </td>
                        </tr>
                        <tr className="hover:bg-slate-50/30 transition-colors">
                            <td className="px-10 py-7">
                                <p className="text-[15px] font-semibold text-slate-900">{t('notificationsSettings.credit.title')}</p>
                                <p className="text-sm text-slate-500 mt-1">{t('notificationsSettings.credit.desc')}</p>
                            </td>
                            <td className="px-6 py-7 text-center">
                                <input defaultChecked className="w-5 h-5 cursor-pointer rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/>
                            </td>
                            <td className="px-10 py-7 text-center">
                                <input className="w-5 h-5 cursor-pointer rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/>
                            </td>
                        </tr>
                        <tr className="hover:bg-slate-50/30 transition-colors">
                            <td className="px-10 py-7">
                                <p className="text-[15px] font-semibold text-slate-900">{t('notificationsSettings.updates.title')}</p>
                                <p className="text-sm text-slate-500 mt-1">{t('notificationsSettings.updates.desc')}</p>
                            </td>
                            <td className="px-6 py-7 text-center">
                                <input className="w-5 h-5 cursor-pointer rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/>
                            </td>
                            <td className="px-10 py-7 text-center">
                                <input defaultChecked className="w-5 h-5 cursor-pointer rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="p-10 bg-slate-50/40">
                <div className="flex items-center justify-between p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
                            <NotificationsOff />
                        </div>
                        <div>
                            <p className="text-[15px] font-bold text-slate-900 leading-none">{t('notificationsSettings.mute.title')}</p>
                            <p className="text-sm text-slate-500 mt-1.5">{t('notificationsSettings.mute.desc')}</p>
                        </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input className="sr-only peer" type="checkbox"/>
                        <div className="w-12 h-6.5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[22px] peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary h-6.5 w-[46px]"></div>
                    </label>
                </div>
            </div>
        </section>
        <div className="flex items-center justify-end gap-6 mt-10">
            <button className="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">{t('notificationsSettings.discard')}</button>
            <button
                onClick={handleSave}
                disabled={loading}
                className={`px-10 py-3 rounded-lg text-sm font-semibold transition-all shadow-md flex items-center gap-2 ${success ? 'bg-emerald-500 text-white' : 'bg-primary text-white hover:bg-blue-700'}`}
            >
                {loading ? <Loader2 className="animate-spin" size={16} /> : success ? <Check size={16} /> : null}
                {loading ? t('notificationsSettings.saving') : success ? t('notificationsSettings.saved') : t('notificationsSettings.save')}
            </button>
        </div>
      </div>
    </SettingsLayout>
  );
};
